import type { TimeValue } from './types'

/**
 * Format time value to string
 */
export function formatTime(time: TimeValue | null, format: '12' | '24', showSeconds: boolean): string {
  /* v8 ignore next */
  if (!time) { return '' }

  let hours = time.hours
  let period = ''

  if (format === '12') {
    period = hours >= 12 ? ' PM' : ' AM'
    hours = hours % 12 || 12
  }

  const hoursStr = hours.toString().padStart(2, '0')
  const minutesStr = time.minutes.toString().padStart(2, '0')
  const secondsStr = showSeconds && time.seconds !== undefined
    ? `:${time.seconds.toString().padStart(2, '0')}`
    : ''

  return `${hoursStr}:${minutesStr}${secondsStr}${period}`
}

/**
 * Parse time string to TimeValue
 */
export function parseTime(input: string, format: '12' | '24'): TimeValue | null {
  const trimmed = input.trim().toUpperCase()
  if (!trimmed) { return null }

  const match24 = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  const match12 = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i)

  if (format === '24' && match24) {
    return parse24HourMatch(match24)
  }

  if (format === '12' && match12) {
    return parse12HourMatch(match12)
  }

  return null
}

function parse24HourMatch(match: RegExpMatchArray): TimeValue | null {
  /* v8 ignore next 7 -- regex guarantees valid capture groups and ranges */
  const hours = parseInt(match[1] ?? '', 10)
  const minutes = parseInt(match[2] ?? '', 10)
  const seconds = match[3] ? parseInt(match[3], 10) : undefined

  if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
    const result: TimeValue = { hours, minutes }
    if (seconds !== undefined) { result.seconds = seconds }
    return result
  }

  return null
}

function adjustHoursForPeriod(hours: number, period: string): number {
  if (period === 'PM' && hours !== 12) { return hours + 12 }
  /* v8 ignore next */
  if (period === 'AM' && hours === 12) { return 0 }
  return hours
}

function parse12HourMatch(match: RegExpMatchArray): TimeValue | null {
  /* v8 ignore next 4 */
  const hoursRaw = parseInt(match[1] ?? '', 10)
  const minutes = parseInt(match[2] ?? '', 10)
  const seconds = match[3] ? parseInt(match[3], 10) : undefined
  const period = (match[4] ?? '').toUpperCase()

  if (hoursRaw < 1 || hoursRaw > 12 || minutes < 0 || minutes > 59) { return null }
  const hours = adjustHoursForPeriod(hoursRaw, period)
  const result: TimeValue = { hours, minutes }
  /* v8 ignore next */
  if (seconds !== undefined) { result.seconds = seconds }
  return result
}

export function generateHours(format: '12' | '24'): number[] {
  if (format === '12') {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
  }
  return Array.from({ length: 24 }, (_, i) => i)
}

export function generateMinutes(minuteStep: number): number[] {
  return Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep)
}
